import { Configuration, fetchOptions, OptionItem } from "@interface48/api";
import { ListSubheader } from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";
import { default as MuiSelect } from "@material-ui/core/Select";
import React from "react";
import { SelectRemoteOptions, SelectValue } from "./models";
import { getSelectedOptionItem } from "./utils";

const UNSPECIFIED_OPTION_VALUE = "";
const UNSPECIFIED_OPTION_LABEL = "(Unspecified)";

interface SelectProps<T, L extends string | JSX.Element> {
  id: string;
  label?: string;
  placeholder?: string;
  autofocus?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  multiple?: boolean;
  value?: SelectValue<T>;
  enumDisabled?: Array<T>;
  options?: Array<OptionItem<T, L>>;
  optionGroups?: Array<{ name: string; values: T[] }>;
  remoteOptions?: SelectRemoteOptions;
  className?: string;
  getApiConfiguration?: () => Promise<Configuration>;
  processValue?: (value: T) => T | null | undefined;
  onChange: (value: SelectValue<T>, id?: string, otherData?: any) => void;
  onBlur?: (value: SelectValue<T>, otherData?: any) => void;
  onFocus?: (value: SelectValue<T>, otherData?: any) => void;
}

interface SelectState<T, L extends string | JSX.Element> {
  value?: SelectValue<T>;
  optionItems: Array<OptionItem<T, L>>;
  selectedOptionItem?: OptionItem<T, L> | Array<OptionItem<T, L>>;
}

export const SelectComponent = class<
  T extends string | number,
  L extends string | JSX.Element = string
> extends React.Component<SelectProps<T, L>, SelectState<T, L>> {
  constructor(props: SelectProps<T, L>) {
    super(props);

    const { required, multiple, value, options, remoteOptions } = this.props;

    // If a Remote URL has been specified, then options will be fetched when mounted, and use a copy of the options
    // provided when applicable as we may end up mutating the array
    const optionItems = this.processOptionItems(remoteOptions ? [] : options ? [...options] : [], required, multiple);

    const selectedOptionItem = getSelectedOptionItem(optionItems, value, multiple);

    this.state = { value, optionItems, selectedOptionItem };
  }

  public async componentDidMount() {
    const { remoteOptions, getApiConfiguration } = this.props;
    const { value } = this.state;

    // If the Select has Remote Options...
    if (remoteOptions) {
      await this.loadRemoteOptions(value, remoteOptions, getApiConfiguration);
    }
  }

  public render() {
    const { id, autofocus, disabled, readonly, multiple, placeholder, optionGroups, className } = this.props;
    const { value, optionItems } = this.state;

    let menuItems: JSX.Element[] = [];

    if (optionGroups) {
      optionGroups.forEach((og, ogIndex) => {
        const groupOptionItems = optionItems.filter(oi => og.values.indexOf(oi.value) !== -1);

        if (groupOptionItems.length) {
          menuItems.push(
            <ListSubheader key={`g-${ogIndex}`} style={{ backgroundColor: "white" }}>
              {og.name}
            </ListSubheader>,
          );
          menuItems.push(...groupOptionItems.map((oi, oiIndex) => this.toMenuItem(oi, `g-${ogIndex}-${oiIndex}`)));
        }
      });

      const allGroupValues = optionGroups.flatMap(og => og.values);
      const otherOptionItems = optionItems.filter(oi => allGroupValues.indexOf(oi.value) === -1);

      if (otherOptionItems.length) {
        menuItems.push(
          <ListSubheader key={"g-other"} style={{ backgroundColor: "white" }}>
            Other
          </ListSubheader>,
        );
        menuItems.push(...otherOptionItems.map((oi, oiIndex) => this.toMenuItem(oi, `g-other-${oiIndex}`)));
      }
    } else {
      menuItems = optionItems.map((oi, oiIndex) => this.toMenuItem(oi, oiIndex));
    }

    return (
      <MuiSelect
        id={id}
        autoFocus={autofocus}
        disabled={disabled || readonly}
        multiple={multiple}
        placeholder={placeholder}
        value={multiple ? value ?? [] : value ?? ""}
        className={className}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
      >
        {menuItems}
      </MuiSelect>
    );
  }

  public async componentDidUpdate(prevProps: SelectProps<T, L>) {
    const { value: prevValue, options: prevOptions, remoteOptions: prevRemoteOptions } = prevProps;
    const { value, multiple, options, remoteOptions, getApiConfiguration } = this.props;
    const { optionItems } = this.state;

    // If the Options were updated via props...
    if (options && prevOptions !== options) {
      this.loadOptions(value, options);
    }
    // Otherwise, if the Select has Remote Options, check to see if the URL was updated via props...
    else if (prevRemoteOptions && remoteOptions && prevRemoteOptions.url !== remoteOptions.url) {
      this.loadRemoteOptions(value, remoteOptions, getApiConfiguration);
    }
    // Otherwise, if the Value was updated via props...
    else if (prevValue !== value) {
      const nextSelectedOptionItem = getSelectedOptionItem(optionItems, value, multiple);

      this.setState({
        ...this.state,
        value,
        selectedOptionItem: nextSelectedOptionItem,
      });
    }
  }

  public handleChange = (event: any) => {
    const { onChange } = this.props;

    const value = this.processSelectedOptionValue(event.target.value);

    this.setState({ ...this.state, value }, () => {
      onChange(value);
    });
  };

  public handleBlur = (event: any) => {
    const { onChange, onBlur } = this.props;
    const { value } = this.state;

    const nextValue = this.processSelectedOptionValue(value);

    if (onBlur) {
      onBlur(nextValue);
    }

    onChange(nextValue);
  };

  public processOptionItems = (
    optionItems: Array<OptionItem<T, L>>,
    required: boolean | undefined,
    multiple: boolean | undefined,
  ) => {
    // If this is a Single Select with an optional value, then add an unspecified option, so that the user can
    // "clear" their selection...
    if (!required && !multiple && optionItems.findIndex(o => o.value === "") === -1) {
      const unspecifiedOptionItem = { value: UNSPECIFIED_OPTION_VALUE, label: UNSPECIFIED_OPTION_LABEL } as OptionItem<
        T,
        L
      >;

      optionItems.unshift(unspecifiedOptionItem);
    }

    return optionItems;
  };

  public processSelectedOptionValue = (selectedOptionValue?: SelectValue<T>): SelectValue<T> => {
    const { processValue, multiple } = this.props;

    if (multiple) {
      const multipleSelectedOptionValues = (selectedOptionValue || []) as Array<T>;

      return multipleSelectedOptionValues.map(selectedOptionValue => {
        const processedValue = processValue ? processValue(selectedOptionValue as T) : selectedOptionValue;

        if (processedValue == null) {
          throw Error("null or undefined key values not supported for multiple Select.");
        }

        return processedValue;
      });
    } else {
      const singleSelectedOptionValue = selectedOptionValue as SelectValue<T>;

      return processValue ? processValue(singleSelectedOptionValue as T) : singleSelectedOptionValue;
    }
  };

  public loadOptions = (value: SelectValue<T> | undefined, options: Array<OptionItem<T, L>>) => {
    const { required, multiple } = this.props;

    const nextOptionItems = this.processOptionItems(options, required, multiple);

    const nextSelectedOptionItem = getSelectedOptionItem(nextOptionItems, value, multiple);

    this.setState({ ...this.state, value, optionItems: nextOptionItems, selectedOptionItem: nextSelectedOptionItem });
  };

  public loadRemoteOptions = async (
    value: SelectValue<T> | undefined,
    remoteOptions: SelectRemoteOptions,
    getApiConfiguration?: () => Promise<Configuration>,
  ) => {
    const { id, label } = this.props;

    const isAsyncSelect = (remoteOptions as any).limit != null;

    if (isAsyncSelect) {
      throw Error("Select does not support Async Remote Options.");
    }

    const selectRemoteOptions = remoteOptions as SelectRemoteOptions;

    try {
      const options = await fetchOptions<T>(
        selectRemoteOptions.url,
        selectRemoteOptions.valueType,
        selectRemoteOptions.defaultLabel,
        selectRemoteOptions.sort,
        getApiConfiguration,
      );

      this.loadOptions(value, options as OptionItem<T, L>[]);
    } catch (error) {
      console.error(error);

      alert(
        `An error occurred upon attempting to initialize the ${label || id} field.\n\n` +
          `Please reload the page and try again. If the error persists, please contact an Administrator for assistance.`,
      );
    }
  };

  public toMenuItem = (optionItem: OptionItem<T, L>, key: React.Key) => {
    const { enumDisabled } = this.props;

    const isDisabled = enumDisabled && enumDisabled.indexOf(optionItem.value) !== -1;

    return (
      <MenuItem key={key} value={optionItem.value} disabled={isDisabled}>
        {optionItem.value === "" && typeof optionItem.label === "string" ? (
          <em>{optionItem.label}</em>
        ) : (
          optionItem.label
        )}
      </MenuItem>
    );
  };
};

export const Select = SelectComponent;
